import type {
  RoutePlan,
  RoutePlanEndpoint,
  RoutePlanLeg,
  RoutePlanLegInput,
  RoutePlanLegSource,
  RoutePlanStopInput,
  RoutePlanStopRole,
} from '@/services/api/routePlans';
import {
  defaultIntentFor,
  deriveStopBilling,
  kindOf,
  roleOf,
  sumChargedLegs,
  type RouteStopIntent,
  type RouteStopKind,
  type RouteStopKindInput,
  type RouteStopType,
  type StopBilling,
} from '@/services/api/routeStopKind';
import type { WorkflowSummary } from '@/services/api/stopWorkflows';

/**
 * Pure (React-free) model + helpers behind the `RoutePlanBuilder` (CR-2): the
 * ordered stop chain a planner edits, the charged-km rule (CR2-D7) and the map
 * to the `{ stops, legs }` create/update payload. Kept out of the component so
 * the risk-bearing bits — re-sequencing on reorder / remove, which legs count
 * as pool legs, applying server-computed km without clobbering a hand edit — are
 * unit-tested without driving a drawer.
 *
 * CR-6 (§22): a stop is `type` + `intent` + `pool` (the workflow vocabulary);
 * the legacy `role` is derived via `roleOf` only where the wire still wants it.
 *
 * CR-8b: a route always starts at the pool and ends back at it, so the pool
 * is the FIRST and the LAST stop by definition and there is no pool switch.
 * `charged` (null = follow the client rule) is meaningful on those two edge
 * stops only; `pool` is DERIVED from position + effective billing
 * (`derivedKind`) and never edited. `kmCharged` counts a leg when BOTH its
 * endpoints are billed (D4); every stop reaches the shipment regardless (D5).
 */

// ─────────────────────────────────────────────────────────────────────────────
// Draft
// ─────────────────────────────────────────────────────────────────────────────

export interface PlanRowDraft extends RouteStopKind {
  uid: string;
  /**
   * CR-8b: billing override, meaningful on the first / last stop only.
   * `null` = follow the client rule. A stop that moves to the middle keeps
   * the stored value but it is ignored (and sent as `null`).
   */
  charged: boolean | null;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  /** Lane pointer — `null` for a manual stop (written back as DRAFT on save). */
  addressId: string | null;
  endpoint: RoutePlanEndpoint | null;
  /**
   * CR-5 workflow pin — an ACTIVE workflow of the plan's client whose stopType
   * is this stop's `type` (and whose intent fits); `null` = follow the client
   * default at shipment creation. Since CR8-D5 every stop, pool included,
   * reaches the shipment, so a pin on a pool stop is always used.
   */
  workflowId: string | null;
  /** Display name of the pin (server refreshes it from the master on save). */
  workflowName: string | null;
}

export interface PlanLegDraft {
  fromUid: string;
  toUid: string;
  /** Canonical decimal string (`"84.6"`); `""` = unknown. */
  km: string;
  /** How the km was measured; `null` until the server answers. */
  source: RoutePlanLegSource | null;
  /** Typed by ops — the server recompute never overwrites a manual km. */
  edited: boolean;
}

export interface PlanDraft {
  stops: PlanRowDraft[];
  legs: PlanLegDraft[];
}

let uidCounter = 0;
const nextUid = () => `plan-stop-${++uidCounter}`;

/**
 * A fresh row. `kind` may carry the new keys, a legacy `role`, or both — it is
 * normalised through `kindOf`, so `{ type: 'DROP_OFF' }` and
 * `{ role: 'DROP_OFF' }` build the same row. The `pool` it carries is only a
 * seed for the intent default; position decides the real value (CR-8b).
 */
export const makePlanRow = (
  kind: RouteStopKindInput,
  patch: Partial<Omit<PlanRowDraft, keyof RouteStopKind>> = {}
): PlanRowDraft => {
  const k = kindOf(kind);
  return {
    uid: nextUid(),
    ...k,
    charged: typeof kind.charged === 'boolean' ? kind.charged : null,
    name: '',
    address: '',
    addressId: null,
    endpoint: null,
    workflowId: null,
    workflowName: null,
    ...patch,
  };
};

/** The legacy role of a row (for the wire and anything still reading it). */
export const rowRole = (row: RouteStopKind): RoutePlanStopRole => roleOf(row);

/**
 * Every stop may pin a workflow — pool legs included: since CR8-D5 every stop
 * reaches the shipment, so the pin is always used by the driver.
 */
export const canPinWorkflow = (_row: Pick<PlanRowDraft, 'pool'>): boolean =>
  true;

// ─────────────────────────────────────────────────────────────────────────────
// CR-8b derivation — pool + billing from position
// ─────────────────────────────────────────────────────────────────────────────

export type DerivedKind = StopBilling;

/**
 * Per row, in stop order: `{ isEdge, effectiveCharged, pool, override }`
 * (see `deriveStopBilling`). Recomputed from position on every render and
 * every payload, so reordering / deleting / inserting moves the edges along.
 */
export const derivedKind = (
  draft: Pick<PlanDraft, 'stops'>,
  clientDefault: boolean
): DerivedKind[] => deriveStopBilling(draft.stops, clientDefault);

/** The rows with their `pool` replaced by the derived value (labels, roles, payload). */
export const withDerivedPool = (
  draft: Pick<PlanDraft, 'stops'>,
  clientDefault: boolean
): PlanRowDraft[] => {
  const d = derivedKind(draft, clientDefault);
  return draft.stops.map((s, i) => (s.pool === d[i].pool ? s : { ...s, pool: d[i].pool }));
};

/** CR-8b: the effective billing of the row at `index` given the client rule. */
export const rowCharged = (
  draft: Pick<PlanDraft, 'stops'>,
  index: number,
  clientDefault: boolean
): boolean => derivedKind(draft, clientDefault)[index]?.effectiveCharged ?? true;

/** CR-8 (D3): an edge row whose effective billing differs from the client rule. */
export const rowChargedOverride = (
  draft: Pick<PlanDraft, 'stops'>,
  index: number,
  clientDefault: boolean
): boolean => derivedKind(draft, clientDefault)[index]?.override ?? false;

/** Any edge stop overrides the client rule (drives the km-summary tag). */
export const planChargedOverride = (
  draft: Pick<PlanDraft, 'stops'>,
  clientDefault: boolean
): boolean => derivedKind(draft, clientDefault).some((d) => d.override);

/**
 * Every stop derived as pool — only possible on a two-stop route whose both
 * edges are unbilled. The server rejects it (no billable pickup / drop), so
 * the builder shows a local hint.
 */
export const planAllPool = (
  draft: Pick<PlanDraft, 'stops'>,
  clientDefault: boolean
): boolean =>
  draft.stops.length > 0 &&
  derivedKind(draft, clientDefault).every((d) => d.pool);

/**
 * The client's ACTIVE workflows a stop may pin, filtered by `(type, intent)`
 * directly (CR6-D1 — no role → stopType mapping): its type, and its intent or
 * a GENERIC workflow of that type (which fits any intent — the server validates
 * stopType only). Defaults first so the "(default)" row is the one ops sees at
 * the top.
 */
export const workflowCandidatesFor = (
  stop: Pick<PlanRowDraft, 'type' | 'intent'> & { pool?: boolean },
  workflows: WorkflowSummary[]
): WorkflowSummary[] => {
  return workflows
    .filter((w) => w.stopType === stop.type)
    .filter((w) => w.intent === stop.intent || w.intent === 'GENERIC')
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
};

/** Drop every pin — the plan moved to another client, whose workflows differ. */
export const clearWorkflowPins = (draft: PlanDraft): PlanDraft => {
  if (draft.stops.every((s) => s.workflowId == null)) return draft;
  return {
    ...draft,
    stops: draft.stops.map((s) =>
      s.workflowId == null ? s : { ...s, workflowId: null, workflowName: null }
    ),
  };
};

/**
 * Rebuild the leg list for the CURRENT consecutive stop pairs, keeping the km /
 * source / edited flag of any pair that still exists. Runs after every reorder /
 * add / remove so a leg never points at a stop that moved away.
 */
export const resequencePlanLegs = (
  stops: PlanRowDraft[],
  prev: PlanLegDraft[]
): PlanLegDraft[] => {
  const byPair = new Map(prev.map((l) => [`${l.fromUid}>${l.toUid}`, l]));
  const out: PlanLegDraft[] = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const fromUid = stops[i].uid;
    const toUid = stops[i + 1].uid;
    out.push(
      byPair.get(`${fromUid}>${toUid}`) ?? {
        fromUid,
        toUid,
        km: '',
        source: null,
        edited: false,
      }
    );
  }
  return out;
};

/**
 * The trucking loop ops start from — pool → pickup → drop → pool, expressed
 * as PICKUP, PICKUP, DROP_OFF·UNLOAD, DROP_OFF·GENERIC (CR6-D4). The first and
 * last rows are the pool by position (CR-8b); their `charged` follows the rule.
 */
export const defaultPlanDraft = (): PlanDraft => {
  const stops = [
    makePlanRow({ type: 'PICKUP', pool: true }),
    makePlanRow({ type: 'PICKUP' }),
    makePlanRow({ type: 'DROP_OFF', intent: 'UNLOAD' }),
    makePlanRow({ type: 'DROP_OFF', intent: 'GENERIC', pool: true }),
  ];
  return { stops, legs: resequencePlanLegs(stops, []) };
};

/**
 * Insert a stop — a billable Drop off · Bongkar by default — BEFORE the last
 * stop, so the route still ends at the pool (CR-8b). With fewer than two stops
 * it simply appends.
 */
export const addPlanRow = (
  draft: PlanDraft,
  kind: RouteStopKindInput = { type: 'DROP_OFF' }
): PlanDraft => {
  const row = makePlanRow(kind);
  const at = draft.stops.length >= 2 ? draft.stops.length - 1 : draft.stops.length;
  const stops = [...draft.stops.slice(0, at), row, ...draft.stops.slice(at)];
  return { stops, legs: resequencePlanLegs(stops, draft.legs) };
};

export const removePlanRow = (draft: PlanDraft, uid: string): PlanDraft => {
  const stops = draft.stops.filter((s) => s.uid !== uid);
  return { stops, legs: resequencePlanLegs(stops, draft.legs) };
};

export const movePlanRow = (
  draft: PlanDraft,
  uid: string,
  direction: -1 | 1
): PlanDraft => {
  const index = draft.stops.findIndex((s) => s.uid === uid);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= draft.stops.length) return draft;
  const stops = [...draft.stops];
  [stops[index], stops[target]] = [stops[target], stops[index]];
  return { stops, legs: resequencePlanLegs(stops, draft.legs) };
};

/**
 * Patch one row. Kind changes keep the row consistent (CR6-D4 / CR-8b):
 * - `type` → PICKUP forces GENERIC; → DROP_OFF defaults to GENERIC on the
 *   last stop (the pool return) and UNLOAD elsewhere, unless the patch names
 *   an intent.
 * - A pin is bound to the type: a type change drops it rather than carrying a
 *   pin the server would reject. Intent changes keep it (ops re-picks if the
 *   candidate list changed).
 * - `charged` is stored as given; whether it counts is decided by position
 *   (`derivedKind`), so nothing is cleared here.
 */
export const patchPlanRow = (
  draft: PlanDraft,
  uid: string,
  patch: Partial<PlanRowDraft>
): PlanDraft => ({
  ...draft,
  stops: draft.stops.map((s, i) => {
    if (s.uid !== uid) return s;
    const next: PlanRowDraft = { ...s, ...patch };
    const typeChanged = patch.type !== undefined && patch.type !== s.type;
    const isLast = i === draft.stops.length - 1;
    if (patch.intent === undefined && typeChanged) {
      next.intent = defaultIntentFor(next.type, isLast);
    }
    if (next.type === 'PICKUP') next.intent = 'GENERIC';
    if (patch.workflowId === undefined && typeChanged) {
      next.workflowId = null;
      next.workflowName = null;
    }
    return next;
  }),
});

/** Ops typed a km → edited, frozen against the server recompute. */
export const setPlanLegKm = (
  draft: PlanDraft,
  fromUid: string,
  toUid: string,
  km: string
): PlanDraft => ({
  ...draft,
  legs: draft.legs.map((l) =>
    l.fromUid === fromUid && l.toUid === toUid
      ? { ...l, km, edited: true }
      : l
  ),
});

/**
 * Ops asked for the computed km back on a leg they had typed over — usually
 * because a stop's address moved and the hand-typed km no longer fits. Drops
 * the manual flag and empties the km, so the next server answer fills it
 * (`applyServerLegs` only ever writes non-edited legs).
 */
export const resetPlanLegKm = (
  draft: PlanDraft,
  fromUid: string,
  toUid: string
): PlanDraft => ({
  ...draft,
  legs: draft.legs.map((l) =>
    l.fromUid === fromUid && l.toUid === toUid
      ? { ...l, km: '', source: null, edited: false }
      : l
  ),
});

/**
 * Fill every NON-edited leg from the server-computed legs (returned in stop
 * order, one per consecutive pair). Returns the same draft object when nothing
 * changes so an effect can `onChange` it without looping.
 */
export const applyServerLegs = (
  draft: PlanDraft,
  serverLegs: RoutePlanLeg[]
): PlanDraft => {
  if (serverLegs.length !== draft.legs.length) return draft;
  let changed = false;
  const legs = draft.legs.map((l, i) => {
    if (l.edited) return l;
    const server = serverLegs[i];
    if (!server) return l;
    const km = String(server.km ?? '');
    if (km === l.km && server.source === l.source) return l;
    changed = true;
    return { ...l, km, source: server.source };
  });
  return changed ? { ...draft, legs } : draft;
};

export const isPlanRowLocated = (row: PlanRowDraft): boolean =>
  row.latitude != null &&
  row.longitude != null &&
  Number.isFinite(row.latitude) &&
  Number.isFinite(row.longitude);

/** Every consecutive pair has both endpoints located — safe to compute legs. */
export const allPlanRowsLocated = (draft: PlanDraft): boolean =>
  draft.stops.length >= 2 && draft.stops.every(isPlanRowLocated);

const legKmNumber = (leg: PlanLegDraft): number => {
  const n = Number(leg.km);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Σ every leg. */
export const kmAllLegs = (draft: PlanDraft): number =>
  round1(draft.legs.reduce((sum, l) => sum + legKmNumber(l), 0));

/**
 * Σ legs whose two endpoints are both billed (CR2-D7 / CR6-D3 / CR8-D4
 * `km_charged`) under the CR-8b derivation: a middle stop always is, an edge
 * stop iff `charged ?? clientDefault`. The planner API computes the stored
 * number with `clientDefault = false`; the builder passes the rule it knows.
 */
export const kmCharged = (draft: PlanDraft, clientDefault = false): number =>
  round1(
    sumChargedLegs(
      draft.stops,
      draft.legs.map((l) => ({ from: l.fromUid, to: l.toUid, km: legKmNumber(l) })),
      (s) => s.uid,
      clientDefault
    )
  );

export const PLAN_NEEDS_PICKUP_AND_DROP =
  'Rute butuh minimal satu Pickup dan satu Drop off yang bukan leg pool.';

/**
 * Field-level problems of a plan draft; empty when it is submittable. The
 * validity check (CR6-D3: ≥1 non-pool PICKUP and ≥1 non-pool DROP_OFF) reads
 * the DERIVED pool, so it needs the client rule the builder shows.
 */
export const planDraftErrors = (
  draft: PlanDraft,
  clientDefault = false
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (draft.stops.length < 2) errors.plan = 'Rute minimal dua stop dengan lokasi.';
  const rows = withDerivedPool(draft, clientDefault);
  const hasPickup = rows.some((s) => s.type === 'PICKUP' && !s.pool);
  const hasDrop = rows.some((s) => s.type === 'DROP_OFF' && !s.pool);
  if (draft.stops.length >= 2 && (!hasPickup || !hasDrop))
    errors.plan = PLAN_NEEDS_PICKUP_AND_DROP;
  draft.stops.forEach((s, i) => {
    if (!s.name.trim()) errors[`${s.uid}.name`] = 'Nama tempat wajib diisi';
    if (!s.address.trim() || !isPlanRowLocated(s))
      errors[`${s.uid}.address`] = `Stop ${i + 1} belum punya lokasi`;
  });
  draft.legs.forEach((l) => {
    if (legKmNumber(l) <= 0)
      errors[`${l.fromUid}>${l.toUid}.km`] = 'Km leg wajib > 0';
  });
  return errors;
};

// ─────────────────────────────────────────────────────────────────────────────
// Payload ↔ plan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Draft → the `stops` array the server persists (1-based seq). Sends the
 * workflow vocabulary (`type`/`intent`) plus the DERIVED `pool` and the
 * derived `role` (CR6-D5), and `charged` on every stop: the override (or null)
 * on the first / last stop, always null in the middle (CR-8b). With those two
 * the server's `isCharged(stop, clientDefault)` matches the builder.
 */
export const toStopsPayload = (
  draft: PlanDraft,
  clientDefault = false
): RoutePlanStopInput[] => {
  const derived = derivedKind(draft, clientDefault);
  return draft.stops.map((s, i) => {
    const kind: RouteStopKind = { type: s.type, intent: s.intent, pool: derived[i].pool };
    return {
      seq: i + 1,
      type: s.type,
      intent: s.intent,
      pool: kind.pool,
      charged: derived[i].isEdge ? (s.charged ?? null) : null,
      role: rowRole(kind),
      name: s.name.trim(),
      address: s.address.trim(),
      latitude: s.latitude ?? 0,
      longitude: s.longitude ?? 0,
      addressId: s.addressId,
      endpoint: s.endpoint,
      workflowId: s.workflowId ?? null,
      workflowName: s.workflowId ? (s.workflowName ?? null) : null,
    };
  });
};

/** Draft → the `legs` array (1-based seq; km as decimal STRINGS). */
export const toLegsPayload = (draft: PlanDraft): RoutePlanLegInput[] => {
  const seqOf = new Map(draft.stops.map((s, i) => [s.uid, i + 1]));
  return draft.legs.map((l, i) => ({
    seq: i + 1,
    fromSeq: seqOf.get(l.fromUid) ?? i + 1,
    toSeq: seqOf.get(l.toUid) ?? i + 2,
    km: String(legKmNumber(l)),
    source: l.source ?? 'HAVERSINE',
    edited: l.edited,
  }));
};

/**
 * A stored plan → an editable draft. Reads each stop through `kindOf`, so a
 * pre-CR-6 plan that only carries `role` restores exactly as before. `charged`
 * is taken from the stops; the stored `pool` is ignored — position decides
 * (CR-8b), so the rows come back with the derived value under `clientDefault`.
 */
export const fromPlan = (
  plan: Pick<RoutePlan, 'stops' | 'legs'>,
  clientDefault = false
): PlanDraft => {
  const sorted = [...plan.stops].sort((a, b) => a.seq - b.seq);
  const rows = sorted.map((s) =>
    makePlanRow(s, {
      name: s.name,
      address: s.address,
      latitude: s.latitude,
      longitude: s.longitude,
      addressId: s.addressId ?? null,
      endpoint: s.endpoint ?? null,
      workflowId: s.workflowId ?? null,
      workflowName: s.workflowId ? (s.workflowName ?? null) : null,
    })
  );
  const stops = withDerivedPool({ stops: rows }, clientDefault);
  const uidBySeq = new Map(sorted.map((s, i) => [s.seq, stops[i].uid]));
  const base = resequencePlanLegs(stops, []);
  const legs = base.map((l) => {
    const saved = plan.legs.find(
      (x) =>
        uidBySeq.get(x.fromSeq) === l.fromUid &&
        uidBySeq.get(x.toSeq) === l.toUid
    );
    if (!saved) return l;
    const savedKm = Number(saved.km);
    return Number.isFinite(savedKm) && savedKm > 0
      ? { ...l, km: String(savedKm), source: saved.source, edited: saved.edited }
      : l;
  });
  return { stops, legs };
};

export type { RouteStopIntent, RouteStopType };
